import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, extractError } from "../lib/api";
import { PageHeader, SectionCard, Pill, StatePill } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ChevronDown, Download, Plus, Trash2 } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useLookups } from "../context/LookupOptionsContext";
import { useWS } from "../context/WebSocketContext";
import { hasRole, useAuth, isAdmin, hasPermission } from "../context/AuthContext";
import { isUnloadingSupervisor } from "../lib/roleMeta";
import {
  contractAllocLabel,
  defaultUnloadDestination,
  purchaseDistinctQualities,
  purchaseRequiresUnloaderContractPick,
} from "../lib/unloadingHelpers";
import RecordedUnloadEditor from "../components/RecordedUnloadEditor";
import LabourSurchargeTierFields from "../components/LabourSurchargeTierFields";
import DataTableBlock from "../components/DataTableBlock";
import { usePaginatedList } from "../hooks/usePaginatedList";
import { downloadApiFile } from "../lib/downloadApiFile";
import { LIST_PAGE_SIZE_OPTIONS } from "../lib/listPagination";
import { UNLOADING_REGISTER_COLUMNS } from "./unloadingColumns";
import AdminEntityActions from "../components/AdminEntityActions";
import AdminPurchaseProductionBatch from "../components/AdminPurchaseProductionBatch";
import UnloadingReceiptActions from "../components/UnloadingReceiptActions";
import ProductionBatchSelect from "../components/ProductionBatchSelect";
import { formatListDate, todayIsoDate } from "../lib/purchaseListMeta";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const HOUDIS = ["H-1", "H-2", "H-3", "H-4"];
const UNLOAD_QUEUE_STATES = ["APPROVED", "UNLOADING", "WEIGH_MID", "UNLOADING_HOLD"];
const UNLOAD_PURCHASE_PARAMS = { view: "unloading" };

function fetchUnloadPurchase(purchaseId) {
  return api.get(`/purchases/${purchaseId}`, { params: UNLOAD_PURCHASE_PARAMS });
}

export default function UnloadingPage() {
  const { user } = useAuth();
  const { optionsFor } = useLookups();
  const [searchParams, setSearchParams] = useSearchParams();
  const varieties = optionsFor("paddy_variety");
  const [stacks, setStacks] = useState([]);
  const [pid, setPid] = useState(() => searchParams.get("purchase") || "");
  const [holdReason, setHoldReason] = useState("");
  const [holdBags, setHoldBags] = useState("");
  const [holdBusy, setHoldBusy] = useState(false);
  const [midWeighBusy, setMidWeighBusy] = useState(false);
  const [destinations, setDestinations] = useState([defaultUnloadDestination()]);
  const [purchaseDetail, setPurchaseDetail] = useState(null);
  const [activeTab, setActiveTab] = useState("operations");
  const [holdDialogOpen, setHoldDialogOpen] = useState(false);
  const [registerSelectedId, setRegisterSelectedId] = useState(null);
  const [registerEditOpen, setRegisterEditOpen] = useState(false);
  const [registerPurchaseDetail, setRegisterPurchaseDetail] = useState(null);
  const [labourContractors, setLabourContractors] = useState([]);
  const [labourContractorPartyId, setLabourContractorPartyId] = useState("");
  const [activeLabourContract, setActiveLabourContract] = useState(null);
  const [labourContractorSaving, setLabourContractorSaving] = useState(false);

  const adminUser = isAdmin(user);
  const canEditRegister = adminUser || hasPermission(user, "unloading.create");

  const [trucks, setTrucks] = useState([]);
  const [trucksLoading, setTrucksLoading] = useState(false);
  const [unloadRegisterCount, setUnloadRegisterCount] = useState(0);
  const [productionBatches, setProductionBatches] = useState([]);
  const [unloadingActiveBatch, setUnloadingActiveBatch] = useState(null);

  useEffect(() => {
    const fromUrl = searchParams.get("purchase");
    if (fromUrl && fromUrl !== String(pid)) {
      setPid(String(fromUrl));
      setActiveTab("operations");
    }
  }, [searchParams]);

  useEffect(() => {
    const current = searchParams.get("purchase") || "";
    if (String(pid || "") === current) return;
    const next = new URLSearchParams(searchParams);
    if (pid) next.set("purchase", String(pid));
    else next.delete("purchase");
    setSearchParams(next, { replace: true });
  }, [pid]);

  useEffect(() => {
    if (!hasPermission(user, "labour.work.record")) return;
    api.get("/parties/bootstrap", { params: { kind: "LABOUR_CONTRACTOR", active: "1" } })
      .then((r) => setLabourContractors(r.data?.results || []))
      .catch(() => setLabourContractors([]));
  }, [user]);

  const loadActiveLabourContract = useCallback(async (partyId, workDate) => {
    if (!partyId) {
      setActiveLabourContract(null);
      return null;
    }
    try {
      const { data } = await api.get("/labour-contracts/active", {
        params: { party_id: partyId, work_date: workDate || todayIsoDate() },
      });
      if (!data?.active) {
        setActiveLabourContract(null);
        return null;
      }
      const { active: _active, ...contract } = data;
      setActiveLabourContract(contract);
      return contract;
    } catch {
      setActiveLabourContract(null);
      return null;
    }
  }, []);

  const saveLabourContractor = async (partyId) => {
    if (!pid || !partyId) return false;
    setLabourContractorSaving(true);
    try {
      const { data } = await api.patch(`/unloading/purchase/${pid}/labour-contract`, {
        labour_contractor_party_id: Number(partyId),
      });
      setPurchaseDetail((prev) => {
        const base = prev || trucks.find((p) => String(p.id) === String(pid));
        if (!base) return prev;
        return {
          ...base,
          unloading: {
            ...(base.unloading || {}),
            ...(data.unloading || {}),
            labour_contractor_party_id: data.labour_contractor_party_id,
            labour_contract_id: data.labour_contract_id,
            labour_contractor_name: data.unloading?.labour_contractor_name,
          },
        };
      });
      setTrucks((prev) =>
        prev.map((p) =>
          String(p.id) !== String(pid)
            ? p
            : {
                ...p,
                unloading: {
                  ...(p.unloading || {}),
                  ...(data.unloading || {}),
                  labour_contractor_party_id: data.labour_contractor_party_id,
                  labour_contract_id: data.labour_contract_id,
                  labour_contractor_name: data.unloading?.labour_contractor_name,
                },
              },
        ),
      );
      if (data.sync_warning) {
        toast.warning(`Contractor saved — labour sync: ${data.sync_warning}`);
      } else if (!data.labour_contract_id) {
        toast.warning(
          "Contractor saved, but no active labour contract covers today. Add or extend a contract under Labour Contracts.",
        );
      }
      return true;
    } catch (err) {
      const status = err?.response?.status;
      const message = extractError(err);
      const isNetwork = !err?.response && (message === "Network Error" || err?.code === "ERR_NETWORK");
      if (status !== 404) {
        toast.error(
          isNetwork
            ? "Could not reach server while saving labour contractor — check Wi‑Fi and that the backend is running"
            : message,
        );
      }
      return false;
    } finally {
      setLabourContractorSaving(false);
    }
  };

  const unloadLogList = usePaginatedList({
    path: "/unloading",
    columns: UNLOADING_REGISTER_COLUMNS,
    defaultSort: { id: "created_at", desc: true },
    enabled: activeTab === "register",
  });

  const loadBootstrap = useCallback(() => {
    setTrucksLoading(true);
    api
      .get("/unloading/bootstrap", { params: { states: UNLOAD_QUEUE_STATES.join(",") } })
      .then((r) => {
        setTrucks(r.data.trucks || []);
        setStacks(r.data.stacks || []);
        setUnloadRegisterCount(r.data.unloading_register_count ?? 0);
        setProductionBatches(r.data.production_batches || []);
        setUnloadingActiveBatch(r.data.unloading_active_batch || null);
      })
      .catch(() => {
        setTrucks([]);
        setStacks([]);
        setUnloadRegisterCount(0);
        setProductionBatches([]);
        setUnloadingActiveBatch(null);
      })
      .finally(() => setTrucksLoading(false));
  }, []);

  const reloadAll = useCallback(() => {
    loadBootstrap();
    if (activeTab === "register") unloadLogList.reload();
  }, [loadBootstrap, activeTab, unloadLogList.reload]);

  useEffect(() => {
    reloadAll();
  }, [reloadAll]);

  const { subscribe } = useWS();
  useEffect(() => subscribe("stack", reloadAll), [subscribe, reloadAll]);
  useEffect(() => subscribe("purchase", reloadAll), [subscribe, reloadAll]);

  useEffect(() => {
    if (!pid) {
      setPurchaseDetail(null);
      return;
    }
    fetchUnloadPurchase(pid)
      .then((r) => setPurchaseDetail(r.data))
      .catch(() => setPurchaseDetail(null));
  }, [pid, trucks, unloadLogList.rows]);

  const selectedPurchase = purchaseDetail || trucks.find((p) => String(p.id) === String(pid));

  const labourContractorName = useMemo(() => {
    if (!labourContractorPartyId) return "";
    const match = labourContractors.find((p) => String(p.id) === String(labourContractorPartyId));
    if (match?.display_name) return match.display_name;
    return selectedPurchase?.unloading?.labour_contractor_name || "";
  }, [labourContractorPartyId, labourContractors, selectedPurchase?.unloading?.labour_contractor_name]);

  useEffect(() => {
    const partyId = selectedPurchase?.unloading?.labour_contractor_party_id;
    setLabourContractorPartyId(partyId ? String(partyId) : "");
    if (partyId) {
      loadActiveLabourContract(partyId);
    } else {
      setActiveLabourContract(null);
    }
  }, [selectedPurchase?.id, selectedPurchase?.unloading?.labour_contractor_party_id, loadActiveLabourContract]);

  const contractAllocs = selectedPurchase?.contract_allocations || [];
  const batchAllocs =
    selectedPurchase?.production_batch_allocations
    || selectedPurchase?.unloading?.production_batch_allocations
    || [];
  const truckQualities = purchaseDistinctQualities(selectedPurchase);
  const truckQuality = truckQualities[0] || selectedPurchase?.variety || "";
  const requiresContractPick = purchaseRequiresUnloaderContractPick(selectedPurchase);
  const multiContractSameQuality = contractAllocs.length > 1 && !requiresContractPick;
  const unloadByQtl = Boolean(selectedPurchase?.unload_by_qtl);
  const recordedDests = selectedPurchase?.unloading?.destinations || [];
  const hasRecordedUnload = recordedDests.length > 0;
  const canRecordUnload = selectedPurchase && ["APPROVED", "UNLOADING"].includes(selectedPurchase.state);
  const canRequestMid =
    selectedPurchase?.state === "UNLOADING" && hasRecordedUnload;
  const atWeighbridgeMid = selectedPurchase?.state === "WEIGH_MID";
  const operationalTrucks = useMemo(() => {
    return trucks.filter((p) => p.state !== "UNLOADING_HOLD");
  }, [trucks]);
  const unloadLogTable = unloadLogList.table;
  const [registerExporting, setRegisterExporting] = useState(false);

  const registerExportParams = useMemo(() => {
    const { page, page_size, export: _exportFlag, ...rest } = unloadLogList.exportParams || {};
    return rest;
  }, [unloadLogList.exportParams]);

  const exportUnloadingRegister = async (mode) => {
    setRegisterExporting(true);
    try {
      const stamp = Date.now();
      if (mode === "excel") {
        await downloadApiFile(
          "/unloading/export/excel",
          `unloading-register-${stamp}.csv`,
          registerExportParams,
        );
        toast.success("Excel (CSV) downloaded");
      } else {
        await downloadApiFile(
          "/unloading/export/pdf",
          `unloading-register-${stamp}.pdf`,
          registerExportParams,
        );
        toast.success("PDF downloaded");
      }
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setRegisterExporting(false);
    }
  };

  const unloadRegisterTabCount = activeTab === "register"
    ? unloadLogList.meta.count
    : unloadRegisterCount;

  const canHoldUnload =
    hasPermission(user, "unloading.create")
    && selectedPurchase
    && ["APPROVED", "UNLOADING"].includes(selectedPurchase.state);

  const registerSelected = unloadLogList.table.rows.find(
    (r) => String(r.id) === String(registerSelectedId),
  );

  useEffect(() => {
    if (!registerSelectedId) {
      setRegisterPurchaseDetail(null);
      return;
    }
    const row = unloadLogList.table.rows.find((r) => String(r.id) === String(registerSelectedId));
    if (!row?.purchase_id) return;
    fetchUnloadPurchase(row.purchase_id)
      .then((r) => setRegisterPurchaseDetail(r.data))
      .catch(() => setRegisterPurchaseDetail(null));
  }, [registerSelectedId, unloadLogList.table.rows]);

  useEffect(() => {
    if (!pid) return;
    const purchaseForDest = {
      ...(selectedPurchase || {}),
      unloading_active_batch_id: unloadingActiveBatch?.id,
    };
    setDestinations([
      defaultUnloadDestination({
        loose: unloadByQtl,
        stacks,
        allocations: contractAllocs,
        purchase: purchaseForDest,
      }),
    ]);
  }, [pid, unloadByQtl, contractAllocs.length, selectedPurchase?.id, selectedPurchase?.production_batch_id, requiresContractPick, unloadingActiveBatch?.id]);

  const addDest = () => {
    const purchaseForDest = {
      ...(selectedPurchase || {}),
      unloading_active_batch_id: unloadingActiveBatch?.id,
    };
    const row = defaultUnloadDestination({
      loose: unloadByQtl,
      stacks,
      allocations: contractAllocs,
      purchase: purchaseForDest,
    });
    const prevBatch = destinations[destinations.length - 1]?.production_batch_id;
    setDestinations([
      ...destinations,
      {
        ...row,
        location_type: "STACK",
        location_id: stacks[0]?.stack_id || "S-A1",
        production_batch_id: prevBatch || row.production_batch_id,
        ...(unloadByQtl ? {} : { bags: "" }),
      },
    ]);
  };
  const onContractPick = (i, contractId) => {
    const alloc = contractAllocs.find((a) => String(a.purchase_contract_id) === String(contractId));
    update(i, {
      purchase_contract_id: contractId,
      variety: alloc?.variety || destinations[i]?.variety,
    });
  };
  const removeDest = (i) => setDestinations(destinations.filter((_, idx) => idx !== i));
  const update = (i, patch) => setDestinations(destinations.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));

  const submit = async (e) => {
    e.preventDefault();
    if (!pid) return toast.error("Select a purchase first");
    const payloadDests = destinations.map((d) => {
      const alloc = requiresContractPick
        ? contractAllocs.find((a) => String(a.purchase_contract_id) === String(d.purchase_contract_id))
        : null;
      const row = {
        location_type: d.location_type,
        location_id: d.location_id,
        variety: alloc?.variety || truckQuality || d.variety,
        bags: unloadByQtl ? 0 : Number(d.bags) || 0,
        weight_qtl: unloadByQtl ? 0 : Number(d.weight_qtl) || 0,
      };
      if (requiresContractPick && d.purchase_contract_id) {
        row.purchase_contract_id = Number(d.purchase_contract_id);
      } else if (contractAllocs.length === 1 && contractAllocs[0]?.purchase_contract_id) {
        row.purchase_contract_id = Number(contractAllocs[0].purchase_contract_id);
      }
      if (adminUser && d.production_batch_id) {
        row.production_batch_id = Number(d.production_batch_id);
      }
      row.stack_height_tier = d.stack_height_tier || "BASE";
      row.carry_distance_tier = d.carry_distance_tier || "BASE";
      return row;
    });
    if (requiresContractPick && payloadDests.some((d) => !d.purchase_contract_id)) {
      return toast.error("Select which quality / contract you are unloading to");
    }
    if (!unloadByQtl && payloadDests.some((d) => !d.bags || d.bags <= 0)) {
      return toast.error("Enter bags for each destination");
    }
    if (payloadDests.length > 1) {
      toast.message("Tip: use one location per submit if you need separate mid weights per location.");
    }
    try {
      await api.post("/unloading", {
        purchase_id: Number(pid),
        destinations: payloadDests,
        labour_contractor_party_id: labourContractorPartyId ? Number(labourContractorPartyId) : undefined,
      });
      toast.success(
        payloadDests.length === 1
          ? `Recorded ${payloadDests[0].location_type} ${payloadDests[0].location_id} — use Mid weigh if this location needs its own bridge weight`
          : "Unloading recorded",
      );
      setDestinations([
        defaultUnloadDestination({
          loose: unloadByQtl,
          stacks,
          allocations: contractAllocs,
          purchase: {
            ...(selectedPurchase || {}),
            unloading_active_batch_id: unloadingActiveBatch?.id,
          },
        }),
      ]);
      const { data } = await fetchUnloadPurchase(pid);
      setPurchaseDetail(data);
      reloadAll();
    } catch (err) {
      toast.error(extractError(err));
    }
  };
  const canManageHold = hasRole(user, ["manager", "admin"]);

  const holdUnloading = async () => {
    if (!pid) return toast.error("Select a purchase first");
    if (!holdReason.trim()) return toast.error("Enter a hold reason");
    try {
      setHoldBusy(true);
      await api.post(`/purchases/${pid}/hold`, {
        purchase_id: Number(pid),
        hold_reason: holdReason,
        hold_bags_count: holdBags ? Number(holdBags) : null,
      });
      toast.success("Unloading put on hold");
      setHoldReason("");
      setHoldBags("");
      setHoldDialogOpen(false);
      reloadAll();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setHoldBusy(false);
    }
  };

  const resumeUnloading = async (purchaseId) => {
    try {
      setHoldBusy(true);
      await api.post(`/purchases/${purchaseId}/resume`);
      toast.success("Unloading resumed");
      reloadAll();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setHoldBusy(false);
    }
  };

  const sendToRelab = async (purchase) => {
    try {
      setHoldBusy(true);
      await api.post(`/purchases/${purchase.id}/request-relab`);
      toast.success("Sent to relab queue");
      reloadAll();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setHoldBusy(false);
    }
  };

  const sendToBargaining = async (purchaseId) => {
    try {
      setHoldBusy(true);
      await api.post(`/purchases/transition?purchase_id=${purchaseId}&to_state=BARGAINING`);
      toast.success("Moved to bargaining queue");
      reloadAll();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setHoldBusy(false);
    }
  };

  const requestMidWeigh = async () => {
    if (!pid) return toast.error("Select a purchase first");
    if (!labourContractorPartyId) return toast.error("Select a labour contractor before mid weigh");
    try {
      setMidWeighBusy(true);
      if (!selectedPurchase?.unloading?.labour_contractor_party_id) {
        await saveLabourContractor(labourContractorPartyId);
      }
      await api.post(`/purchases/${pid}/request-mid-weigh`);
      toast.success("Sent to weighbridge for mid weight");
      const { data } = await fetchUnloadPurchase(pid);
      setPurchaseDetail(data);
      reloadAll();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setMidWeighBusy(false);
    }
  };

  const rejectVehicle = async (purchaseId) => {
    try {
      setHoldBusy(true);
      await api.post(`/purchases/transition?purchase_id=${purchaseId}&to_state=REJECTED`);
      toast.success("Vehicle rejected");
      reloadAll();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setHoldBusy(false);
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="unloading-page">
      <PageHeader
        eyebrow={isUnloadingSupervisor(user) ? "Unloading Supervisor" : "Floor Supervisor"}
        title="Unloading Assignment"
        description="Count bags per location, record unload, request mid weigh when each location needs its own bridge weight; otherwise final tare splits net by bags."
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-card border border-border p-1 h-auto flex-wrap">
          <TabsTrigger value="operations" className="text-xs sm:text-sm">Operations</TabsTrigger>
          <TabsTrigger value="register" className="text-xs sm:text-sm">
            Unloading register ({unloadRegisterTabCount})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="operations" className="space-y-6 sm:space-y-8 mt-0">

      <SectionCard title="Unload at location (Houdi / Stack)" testId="unload-at-location">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {operationalTrucks.map((p) => {
            const allocs = p.contract_allocations || [];
            const selected = String(pid) === String(p.id);
            return (
              <div
                key={p.id}
                className={`flex items-start gap-1 rounded-md border px-3 py-2.5 transition-colors min-w-0 ${
                  selected
                    ? "border-bansal-sage bg-muted/80"
                    : "border-border bg-card hover:bg-muted/40"
                }`}
                data-testid={`unload-truck-card-${p.id}`}
              >
                <button
                  type="button"
                  onClick={() => setPid(String(p.id))}
                  className="min-w-0 flex-1 text-left space-y-1"
                >
                  <p className="text-sm font-medium break-words leading-snug">{p.party_name || "—"}</p>
                  <p className="text-base sm:text-lg font-semibold uppercase tracking-wide tabular">
                    {p.vehicle_number ? String(p.vehicle_number).toUpperCase() : "—"}
                  </p>
                  <div className="space-y-0.5 pt-0.5">
                    {allocs.length > 0 ? (
                      allocs.map((a) => (
                        <p
                          key={a.purchase_contract_id || `${a.variety}-${a.rate_per_qtl}`}
                          className="text-xs text-muted-foreground tabular break-words"
                        >
                          <span className="font-medium text-foreground/80">{a.variety || "—"}</span>
                          {a.rate_per_qtl != null ? ` · ₹${a.rate_per_qtl}/qtl` : ""}
                        </p>
                      ))
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground/80">{p.variety || "—"}</span>
                      </p>
                    )}
                  </div>
                </button>
                {isAdmin(user) && (
                  <div className="shrink-0 -mr-1 -mt-0.5" onClick={(e) => e.stopPropagation()}>
                    <AdminEntityActions
                      entityType="purchase"
                      entityId={p.id}
                      entityLabel={p.code}
                      editTo={`/purchases/${p.id}`}
                      onDeleted={reloadAll}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {operationalTrucks.length === 0 && !trucksLoading && (
          <div className="text-sm text-muted-foreground text-center py-6">
            No trucks pending unload.
          </div>
        )}

        {selectedPurchase && operationalTrucks.some((p) => String(p.id) === String(pid)) && (
          <>
            <div className="mt-5 pt-5 border-t border-border space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <StatePill state={selectedPurchase.state} />
                {unloadByQtl ? (
                  <span className="text-xs text-muted-foreground">
                    <b>Loose</b>
                    {selectedPurchase.gross_weight_qtl ? ` · Gross ${selectedPurchase.gross_weight_qtl} qtl` : ""}
                    {selectedPurchase.loose_unloaded_qtl
                      ? ` · Unloaded ${selectedPurchase.loose_unloaded_qtl} qtl`
                      : ""}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    Net {selectedPurchase.net_weight_qtl ?? "pending"} qtl · {selectedPurchase.bags_reported} bags total
                  </span>
                )}
                <Link to={`/purchases/${selectedPurchase.id}`} className="link-brand font-medium text-xs">
                  Details →
                </Link>
              </div>
              {contractAllocs.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {contractAllocs.map((a) => (
                    <span
                      key={a.purchase_contract_id}
                      className="inline-flex items-center gap-1 text-[10px] bg-card border border-border rounded px-2 py-1"
                    >
                      <span className="font-mono">{a.contract_number || a.purchase_contract_id}</span>
                      <span className="font-medium">{a.variety}</span>
                      <span className="text-muted-foreground">₹{a.rate_per_qtl}/qtl</span>
                      {a.net_weight_qtl > 0 && (
                        <span className="text-primary font-medium tabular">{a.net_weight_qtl} qtl</span>
                      )}
                    </span>
                  ))}
                </div>
              )}
              {batchAllocs.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {batchAllocs.map((a) => (
                    <span
                      key={a.production_batch_id}
                      className="inline-flex items-center gap-1 text-[10px] bg-card border border-amber-200 rounded px-2 py-1"
                    >
                      <span className="font-mono">{a.production_batch_code || a.production_batch_id}</span>
                      {a.bags > 0 && <span className="text-muted-foreground">{a.bags} bags</span>}
                      {a.weight_qtl > 0 && (
                        <span className="text-primary font-medium tabular">{a.weight_qtl} qtl</span>
                      )}
                    </span>
                  ))}
                </div>
              )}
              {labourContractorPartyId && labourContractorName && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">Labour contractor</span>
                  <Pill tone="sage">{labourContractorName}</Pill>
                </div>
              )}
              <AdminPurchaseProductionBatch
                purchase={selectedPurchase}
                onSaved={(p) => {
                  if (p) setPurchaseDetail(p);
                  reloadAll();
                }}
              />
            </div>

        <form onSubmit={submit} className="space-y-5 mt-5" data-testid="unload-form">
          {hasPermission(user, "labour.work.record") && (
            <div className="rounded-md border border-border bg-card p-3 space-y-2">
              <Label className="text-xs font-medium">Labour contractor</Label>
              <Select
                value={labourContractorPartyId}
                disabled={labourContractorSaving}
                onValueChange={(v) => {
                  if (!v) return;
                  setLabourContractorPartyId(v);
                  loadActiveLabourContract(v);
                  saveLabourContractor(v);
                }}
              >
                <SelectTrigger className="w-full max-w-md">
                  <SelectValue placeholder="Select labour contractor (required for mid weigh)" />
                </SelectTrigger>
                <SelectContent>
                  {labourContractors.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {activeLabourContract && (
                <p className="text-[10px] text-muted-foreground">
                  Active contract <span className="font-mono">{activeLabourContract.contract_number}</span>
                  {" "}({activeLabourContract.valid_from}
                  {activeLabourContract.valid_to ? ` → ${activeLabourContract.valid_to}` : ""})
                </p>
              )}
              {labourContractorPartyId && !activeLabourContract && (
                <p className="text-[10px] text-amber-700">No active contract for this contractor today.</p>
              )}
              <p className="text-[10px] text-muted-foreground">
                The active contract for this period is used for rates. Pick stack / carry surcharge tiers on each unload line.
              </p>
            </div>
          )}

          {multiContractSameQuality && (
            <div className="rounded-md border border-sky-200 bg-sky-50/60 px-3 py-2 text-xs text-sky-950">
              Truck quality <strong>{truckQuality}</strong> — {contractAllocs.length} purchase contracts linked.
              Unload normally; <strong>accounts</strong> will split net qtl between contracts before voucher.
            </div>
          )}

          {hasRecordedUnload && selectedPurchase && (
            <RecordedUnloadEditor
              purchase={selectedPurchase}
              stacks={stacks}
              productionBatches={productionBatches}
              conditionRules={activeLabourContract?.condition_rules}
              labourContractorName={labourContractorName}
              onSaved={() => {
                fetchUnloadPurchase(pid).then((r) => setPurchaseDetail(r.data));
                reloadAll();
              }}
            />
          )}

          {atWeighbridgeMid && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Truck is at the <strong>weighbridge</strong> for mid weight. After the operator records it, you can unload the next
              location here.
            </div>
          )}

          <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
              This trip — {requiresContractPick ? "quality / contract, " : ""}location {unloadByQtl ? "(loose)" : "(bags)"}
              {adminUser ? " · mill batch" : ""}
            </div>
            {destinations.map((d, i) => (
              <div
                key={i}
                className="rounded-md p-3 border border-border bg-muted/50 space-y-3 min-w-0"
                data-testid={`dest-${i}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Line {i + 1}
                  </span>
                  {destinations.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-muted-foreground hover:text-red-600"
                      onClick={() => removeDest(i)}
                      aria-label="Remove line"
                      data-testid={`remove-dest-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div
                  className={`grid grid-cols-1 gap-3 ${
                    unloadByQtl ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-4"
                  }`}
                >
                <Field label="Type">
                  <Select value={d.location_type} onValueChange={(v) => update(i, { location_type: v, location_id: v === "HOUDI" ? "H-1" : stacks[0]?.stack_id })}>
                    <SelectTrigger className="w-full min-w-0"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HOUDI">Houdi</SelectItem>
                      <SelectItem value="STACK">Stack</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Location">
                  <Select value={d.location_id} onValueChange={(v) => update(i, { location_id: v })}>
                    <SelectTrigger className="w-full min-w-0"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(d.location_type === "HOUDI" ? HOUDIS : stacks.map((s) => s.stack_id)).map((id) => <SelectItem key={id} value={id}>{id}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                {requiresContractPick ? (
                  <Field label="Quality / contract">
                    <Select
                      value={d.purchase_contract_id ? String(d.purchase_contract_id) : ""}
                      onValueChange={(v) => onContractPick(i, v)}
                    >
                      <SelectTrigger className="w-full min-w-0 [&>span]:truncate" data-testid={`dest-contract-${i}`}>
                        <SelectValue placeholder="Select contract…">
                          {d.purchase_contract_id
                            ? contractAllocLabel(
                                contractAllocs.find(
                                  (a) => String(a.purchase_contract_id) === String(d.purchase_contract_id),
                                ),
                              )
                            : null}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-w-[min(100vw-2rem,28rem)]">
                        {contractAllocs.map((a) => (
                          <SelectItem key={a.purchase_contract_id} value={String(a.purchase_contract_id)}>
                            {contractAllocLabel(a)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                ) : truckQuality ? (
                  <Field label="Quality">
                    <Input value={truckQuality} readOnly className="bg-muted/40" />
                  </Field>
                ) : (
                  <Field label="Variety">
                    <Select value={d.variety} onValueChange={(v) => update(i, { variety: v })}>
                      <SelectTrigger className="w-full min-w-0"><SelectValue /></SelectTrigger>
                      <SelectContent>{varieties.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                )}
                {!unloadByQtl && (
                  <Field label="Bags">
                    <Input type="number" min="0" value={d.bags} onChange={(e) => update(i, { bags: e.target.value })} data-testid={`dest-bags-${i}`} />
                  </Field>
                )}
                </div>
                {adminUser && (
                <Field label="Production batch">
                  <ProductionBatchSelect
                    batches={productionBatches}
                    value={d.production_batch_id}
                    onChange={(v) => update(i, { production_batch_id: v })}
                    testId={`dest-batch-${i}`}
                  />
                </Field>
                )}
                {labourContractorPartyId && activeLabourContract?.condition_rules && (
                  <LabourSurchargeTierFields
                    conditionRules={activeLabourContract.condition_rules}
                    locationType={d.location_type}
                    stackHeightTier={d.stack_height_tier || "BASE"}
                    carryDistanceTier={d.carry_distance_tier || "BASE"}
                    onStackHeightTierChange={(v) => update(i, { stack_height_tier: v })}
                    onCarryDistanceTierChange={(v) => update(i, { carry_distance_tier: v })}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addDest}
              disabled={!canRecordUnload || atWeighbridgeMid}
              data-testid="add-dest"
            >
              <Plus className="h-3 w-3 mr-1" /> Same trip — add line
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={!canRequestMid || midWeighBusy || atWeighbridgeMid}
              onClick={requestMidWeigh}
              className="min-h-[44px] border-bansal-gold bg-amber-50 text-amber-950 hover:bg-amber-100"
              data-testid="request-mid-weigh"
            >
              {midWeighBusy ? "Sending…" : "② Send to weighbridge (mid weight)"}
            </Button>
            {canHoldUnload && (
              <Button
                type="button"
                variant="outline"
                disabled={holdBusy}
                onClick={() => setHoldDialogOpen(true)}
                className="min-h-[44px] border-amber-500 text-amber-700 hover:bg-amber-50"
                data-testid="hold-unloading-open"
              >
                Hold unloading
              </Button>
            )}
            <Button
              type="submit"
              disabled={!canRecordUnload || atWeighbridgeMid}
              className="bg-bansal-sage hover:bg-bansal-sageDark text-white min-h-[44px]"
              data-testid="unload-submit"
            >
              ① Record unload at this location
            </Button>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong>Step 1</strong> — Choose Houdi or Stack, location, and bags (if bagged).
            {adminUser ? " Admin may pick a mill batch per line, or split a recorded unload later." : " Paddy is recorded on the mill batch currently accepting unloading."}
            <br />
            <strong>Step 2</strong> — If this location needs its own bridge weight, tap <strong>Mid weigh</strong> before
            unloading the next place.
            <br />
            Repeat for each location/type. If you only use one final tare (no mid), weights are calculated from bags at the
            end.
          </p>
          {selectedPurchase && !hasRecordedUnload && selectedPurchase.state === "APPROVED" && (
            <p className="text-xs text-amber-800">First action: record unload at location ① — then mid weigh ② becomes available.</p>
          )}
          {canRequestMid && (
            <p className="text-xs link-brand font-medium">Mid weigh is available for this truck — send after each location when needed.</p>
          )}
        </form>
          </>
        )}
      </SectionCard>

      {trucks.filter((p) => p.state === "UNLOADING_HOLD").length > 0 && (
        <SectionCard title={`On Hold (${trucks.filter((p) => p.state === "UNLOADING_HOLD").length})`}>
          <div className="space-y-3">
            {trucks.filter((p) => p.state === "UNLOADING_HOLD").map((p) => (
              <div key={p.id} className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <span className="font-mono text-xs text-muted-foreground mr-2">{p.code}</span>
                    <span className="font-medium">{p.party_name} · {p.variety}</span>
                    {p.assigned_to && <span className="text-xs text-muted-foreground ml-2">(→ {p.assigned_to})</span>}
                  </div>
                  <Link to={`/purchases/${p.id}`} className="text-xs link-brand font-medium shrink-0">
                    View details →
                  </Link>
                </div>
                {p.hold_reason && <div className="mt-2 text-amber-800 text-xs">Hold reason: {p.hold_reason}</div>}
                {canManageHold && (
                  <div className="mt-3 space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Manager decision: resume unloading, reject vehicle, bargain directly, or send for relab first.
                    </p>
                    <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end">
                      <Button
                        onClick={() => rejectVehicle(p.id)}
                        disabled={holdBusy}
                        variant="outline"
                        className="border-red-500 text-red-600 hover:bg-red-50 min-h-[40px]"
                      >
                        Reject Vehicle
                      </Button>
                      <Button
                        onClick={() => sendToRelab(p)}
                        disabled={holdBusy}
                        variant="outline"
                        className="border-blue-500 text-blue-700 hover:bg-blue-50 min-h-[40px]"
                      >
                        Send to Relab
                      </Button>
                      <Button
                        onClick={() => sendToBargaining(p.id)}
                        disabled={holdBusy}
                        variant="outline"
                        className="border-amber-500 text-amber-700 hover:bg-amber-50 min-h-[40px]"
                      >
                        Bargain (No Relab)
                      </Button>
                      <Button
                        onClick={() => resumeUnloading(p.id)}
                        disabled={holdBusy}
                        className="bg-bansal-sage hover:bg-bansal-sageDark text-white min-h-[40px]"
                      >
                        Resume Unloading
                      </Button>
                    </div>
                    <div className="text-[11px] text-muted-foreground text-right">
                      For bargaining cuts/rates, open <Link to={`/purchases/${p.id}`} className="link-brand">purchase details</Link> after moving to bargaining.
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      )}

        </TabsContent>

        <TabsContent value="register" className="mt-0">
          <SectionCard title="Unloading register" testId="unloading-register">
            <p className="text-xs text-muted-foreground mb-3">
              Recorded unload assignments — print or download a slip. Admin can edit destinations even after weighbridge input (register → Edit).
            </p>
            <DataTableBlock
              title={
                Object.keys(unloadLogTable.columnFilters || {}).length > 0
                  ? `${unloadLogList.meta.count} matching unload records`
                  : `${unloadLogList.meta.count} unload records`
              }
              wrapInSectionCard={false}
              columns={UNLOADING_REGISTER_COLUMNS}
              table={unloadLogTable}
              loading={unloadLogList.loading}
              totalCount={unloadLogList.meta.count}
              testId="unloading-register-filters"
              hint="Filter by unload date range, labour name, vehicle, party, and more — exports use the same filters."
              emptyFilteredMessage="No unload records match your filters."
              pagination={{ ...unloadLogList.pagination, pageSizeOptions: LIST_PAGE_SIZE_OPTIONS }}
              minWidth="0"
              exportButton={(
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs gap-1"
                      disabled={registerExporting}
                      data-testid="unloading-register-export"
                    >
                      <Download className="h-3.5 w-3.5" />
                      {registerExporting ? "Exporting…" : "Export"}
                      <ChevronDown className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => exportUnloadingRegister("excel")}
                      data-testid="unloading-register-export-excel"
                    >
                      Export Excel (CSV)
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => exportUnloadingRegister("pdf")}
                      data-testid="unloading-register-export-pdf"
                    >
                      Export PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              renderTrailingHeader={() => (
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Actions</span>
              )}
              renderRow={(u) => {
                const locations =
                  (u.destinations || []).map((d) => d.location_id).filter(Boolean).join(", ") || "—";
                return (
                  <tr
                    key={u.id}
                    className={`border-b border-border/60 hover:bg-muted/20 cursor-pointer ${
                      String(registerSelectedId) === String(u.id) ? "bg-sky-50/60" : ""
                    }`}
                    onClick={() => {
                      setRegisterSelectedId(u.id);
                      setRegisterEditOpen(false);
                    }}
                  >
                    <td className="py-2 px-2 text-xs whitespace-nowrap">{formatListDate(u.created_at) || "—"}</td>
                    <td className="py-2 px-2 text-xs font-medium uppercase">{u.vehicle_number || "—"}</td>
                    <td className="py-2 px-2 text-xs font-mono">{u.purchase_code || "—"}</td>
                    <td className="py-2 px-2 text-xs">{u.party_name || "—"}</td>
                    <td className="py-2 px-2 text-xs">{u.variety || "—"}</td>
                    <td className="py-2 px-2 text-xs">{locations}</td>
                    <td className="py-2 px-2 text-xs tabular">{u.total_bags ?? "—"}</td>
                    <td className="py-2 px-2 text-xs tabular">
                      {u.total_weight_qtl != null ? u.total_weight_qtl : "—"}
                    </td>
                    <td className="py-2 px-2 text-xs">{u.labour_contractor_name || "—"}</td>
                    <td className="py-2 px-2 text-xs">{u.created_by || "—"}</td>
                    <td className="py-2 px-2 text-xs whitespace-nowrap">
                      <UnloadingReceiptActions
                        compact
                        unloadingId={u.id}
                        label={u.purchase_code || u.vehicle_number}
                        canEdit={canEditRegister}
                        onEdit={() => {
                          setRegisterSelectedId(u.id);
                          setRegisterEditOpen(true);
                        }}
                      />
                    </td>
                  </tr>
                );
              }}
            />

            {registerSelected && (
              <div className="mt-4 rounded-md border border-border bg-muted/10 p-4 space-y-4 min-w-0">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{registerSelected.party_name}</p>
                  <p className="text-[10px] text-muted-foreground mt-1 font-mono">
                    {registerSelected.purchase_code || registerSelected.vehicle_number}
                  </p>
                  <div className="text-xs mt-2 flex flex-wrap gap-2">
                    {(registerSelected.destinations || []).map((d, i) => (
                      <span key={i} className="inline-flex items-center gap-1">
                        <Pill tone={d.location_type === "HOUDI" ? "gold" : "sage"}>{d.location_id}</Pill>
                        <span className="text-muted-foreground">
                          {(d.weight_qtl ?? 0) > 0 || !d.bags
                            ? `${d.weight_qtl ?? "—"} qtl`
                            : `${d.bags} bags`}
                          {d.production_batch_code ? ` · ${d.production_batch_code}` : ""}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>

                {canEditRegister && registerEditOpen && registerPurchaseDetail && (
                  <RecordedUnloadEditor
                    purchase={registerPurchaseDetail}
                    stacks={stacks}
                    productionBatches={productionBatches}
                    embedContractorPicker
                    adminOnly
                    onSaved={() => {
                      fetchUnloadPurchase(registerPurchaseDetail.id).then((r) => setRegisterPurchaseDetail(r.data));
                      reloadAll();
                    }}
                  />
                )}
                {isAdmin(user) && registerPurchaseDetail && (
                  <AdminPurchaseProductionBatch
                    purchase={registerPurchaseDetail}
                    onSaved={(p) => {
                      if (p) setRegisterPurchaseDetail(p);
                      else {
                        fetchUnloadPurchase(registerPurchaseDetail.id).then((r) => setRegisterPurchaseDetail(r.data));
                      }
                      reloadAll();
                    }}
                  />
                )}
              </div>
            )}
          </SectionCard>
        </TabsContent>
      </Tabs>

      <Dialog open={holdDialogOpen} onOpenChange={setHoldDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Hold unloading</DialogTitle>
            <DialogDescription>
              If quality inside the truck is not up to mark, hold unloading and notify the purchase manager.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Hold reason</Label>
              <Textarea
                value={holdReason}
                onChange={(e) => setHoldReason(e.target.value)}
                placeholder="Describe the quality issue…"
                className="mt-1.5"
                rows={3}
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {unloadByQtl ? "Affected qtl (optional)" : "Affected bags (optional)"}
              </Label>
              <Input
                type="number"
                step={unloadByQtl ? "0.01" : "1"}
                value={holdBags}
                onChange={(e) => setHoldBags(e.target.value)}
                placeholder={unloadByQtl ? "e.g. 12.5" : "e.g. 25"}
                className="mt-1.5"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setHoldDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={holdUnloading}
              disabled={holdBusy || !holdReason.trim()}
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
              data-testid="hold-unloading-submit"
            >
              {holdBusy ? "Holding…" : "Hold unloading"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
